import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';

import { formatMetric, generateUUID } from '../../../../../utils/util';

import Button from '../../../../common/button/Button';
import TextInput from '../../../../common/inputs/text-input/TextInput';
import FilePicker from '../../../../common/inputs/file-picker/FilerPicker';
import Thumbnails from '../../../../common/thumbnails/Thumbnails';
import PollModal from './poll-modal/PollModal';
import Poll from '../../../../common/poll/Poll';
import ListingCard from '../../../../common/listing/listing-card/ListingCard';

import { ReactComponent as AttachmentIcon } from '../../../../../resources/icons/attachment.svg';
import { ReactComponent as PollIcon } from '../../../../../resources/icons/poll.svg';

import './market-buzz-create-post.scss';

const MarketBuzzCreatePost = ({ post, product, createPost, updatePost }) => {
  const [text, setText] = useState('');
  const [images, setImages] = useState([]);
  const [poll, setPoll] = useState(undefined);
  const [showPollModal, setShowPollModal] = useState(false);

  const fileRef = useRef(null);

  const navigate = useNavigate();

  useEffect(() => {
    if (post) {
      setText(post.text);
      setImages(post.linkedMediaReferences);

      if (!post.question) return;

      const poll = {
        question: post.question,
        options: post.options.map((option) => {
          return { id: generateUUID(), value: option, votes: 0, voted: false };
        }),
      };
      setPoll(poll);
    }
  }, [post]);

  const deleteImage = (index) => {
    const newImages = [...images];
    newImages.splice(index, 1);
    setImages(newImages);
  };

  const handleFileChange = ({ target }) => {
    const file = target.files[0];
    setImages([...images, file]);
  };

  const handlePostClick = () => {
    createPost(text, images, poll);
  };

  const handleSaveClick = () => {
    updatePost(text, images, poll);
  };

  const renderProduct = () => {
    if (!product && post?.productExternalReference) {
      return (
        <ListingCard
          title={post.productName}
          description={post.productPrice}
          images={[post.productPictureExternalReference]}
          post
          hideActions
        />
      );
    }

    if (!product) return null;

    const {
      brandName,
      specification,
      pricePerProductUnit,
      productUnit,
      preferredPrice,
      desiredQuantity,
      desiredQuantityMetric,
      linkedMediaExternalReferences,
    } = product;

    let title = brandName;
    if (!brandName && specification) {
      title = specification.name;
    }

    let description;
    if (pricePerProductUnit && productUnit) {
      description = `${pricePerProductUnit}/${productUnit}`;
    } else {
      description = `${preferredPrice.trim()}${desiredQuantity} ${formatMetric(desiredQuantityMetric)}`;
    }

    return (
      <ListingCard title={title} description={description} images={linkedMediaExternalReferences} post hideActions />
    );
  };

  return (
    <section className="market-buzz-create-post">
      <section className="market-buzz-create-post__header">
        <h1>Market Buzz</h1>
        <button className="market-buzz-create-post__back-button" onClick={() => navigate(-1)}>
          {'<'} Back
        </button>
      </section>

      <section className="market-buzz-create-post__card">
        <h2 className="market-buzz-create-post__card__header">{post ? 'Edit Post' : 'Create Post'}</h2>
        <div className="market-buzz-create-post__card__content">
          <TextInput
            className="market-buzz-create-post__input"
            value={text}
            onChange={({ target }) => setText(target.value)}
            multiline
          />
          {renderProduct()}
          <Thumbnails images={images} deleteImage={deleteImage} />
          <Poll poll={poll} editPoll={() => setShowPollModal(true)} deletePoll={() => setPoll(undefined)} />
        </div>
        <hr className="market-buzz-create-post__separator" />
        <div className="market-buzz-create-post__card__actions">
          <div className="market-buzz-create-post__card__actions__icons">
            <FilePicker
              fileRef={fileRef}
              enableFileLimit={true}
              triggerElement={
                <AttachmentIcon
                  className="market-buzz-create-post__actions__icon"
                  onClick={() => fileRef.current.click()}
                />
              }
              onChange={handleFileChange}
            />
            <PollIcon className="market-buzz-create-post__actions__icon" onClick={() => setShowPollModal(true)} />
          </div>
          {post ? <Button onClick={handleSaveClick}>SAVE</Button> : <Button onClick={handlePostClick}>POST</Button>}
        </div>
      </section>

      <PollModal show={showPollModal} setShow={setShowPollModal} poll={poll} setPoll={setPoll} />
    </section>
  );
};

MarketBuzzCreatePost.propTypes = {
  post: PropTypes.shape({}),
  product: PropTypes.shape({}),
  createPost: PropTypes.func.isRequired,
  updatePost: PropTypes.func.isRequired,
};

MarketBuzzCreatePost.defaultProps = {
  post: undefined,
  product: undefined,
};

export default MarketBuzzCreatePost;
